import React, { useState } from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography, Box, Collapse } from '@mui/material';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { ERouteLinks } from 'models/route';
import { Link } from 'react-router-dom';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ELegalAttachmentsGroupBy } from 'models/legalEntity';
import { getLicenceStatusChipVariant } from '../LegalEntityViewLicencePage/helpers';
import { ATTACHMENT_TYPE_CASE, ATTACHMENT_TYPE_LICENCE } from '../helpers';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';

interface ILegalEntityGroupedAttachmentTableCard {
  data: ITableDataSource;
  groupBy: ELegalAttachmentsGroupBy.ATTACHMENT_TYPE | ELegalAttachmentsGroupBy.NUMBER;
  handleView: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDownload: (
    legalUniqueId: string,
    attachmentUniqueId: string,
    fileName: string,
    licenceUniqueId?: string,
    caseUniqueId?: string,
  ) => void;
  handleDelete: (attachmentUniqueId: string) => void;
  hasPermissionsAttachments: boolean;
  hasPermissionsLicence: boolean;
  type: string | null;
  t: (key: string) => string;
}

const LegalEntityGroupedAttachmentTableCard = ({
  data,
  groupBy,
  handleView,
  handleDownload,
  handleDelete,
  hasPermissionsAttachments,
  hasPermissionsLicence,
  type,
  t,
}: ILegalEntityGroupedAttachmentTableCard) => {
  const { shortDateFormat } = useGlobalProperty();
  const [isExpanded, setIsExpanded] = useState(false);

  const renderDate = (date: string) => formatDate(date, shortDateFormat);
  const details = (data.detailsColumns || data.attachments) as ITableDataSource[];

  return (
    <Grid sx={{ backgroundColor: colors.gray100, border: `1px solid ${colors.gray200}`, borderRadius: '4px', overflow: 'hidden', padding: '20px', rowGap: 2 }}>
      {groupBy === ELegalAttachmentsGroupBy.ATTACHMENT_TYPE && (
        <>
          <Grid size={{ xs: 12 }}>
            <Grid sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <Grid size={{ xs: 10 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.attachments.fields.attachmentType')}
                </Typography>
                <Typography variant="body2">{data.attachmentType}</Typography>
              </Grid>
              <Grid sx={{ textAlign: "right" }}>
                <Button
                  variant={EButtonVariants.text}
                  icon={isExpanded ? EIconTypes.smallDropdownUp : EIconTypes.smallDropdownDown}
                  onClick={() => setIsExpanded(!isExpanded)}
                  tableButton
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.numberOfFiles')}
            </Typography>
            <Typography variant="body2">
              {data.fileCount} {t('legalEntityDetails.attachments.fields.filesUnit')}
            </Typography>
          </Grid>
        </>
      )}
      {groupBy === ELegalAttachmentsGroupBy.NUMBER && (
        <>
          <Grid size={{ xs: 12 }}>
            <Grid sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <Grid size={{ xs: 10 }}>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('legalEntityDetails.attachments.fields.number')}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.primary500, fontWeight: 600 }}>
                  {data.licenceUniqueId && type && hasPermissionsLicence && (
                    <Link
                      to={`${ERouteLinks.LegalEntityViewLicence.replace(
                        ':id',
                        data.legalUniqueId,
                      ).replace(':licenceId', data.licenceUniqueId)}?type=${type}&from=le`}
                      style={{
                        color: colors.primary500,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      {data.parentObjectIdNumber}
                    </Link>
                  )}
                  {data.caseUniqueId && hasPermissionsLicence && (
                    <Link
                      to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                        ':id',
                        data.legalUniqueId,
                      ).replace(':caseId', data.caseUniqueId)}?type=${type}&from=le`}
                      style={{
                        color: colors.primary500,
                        fontWeight: 600,
                        textDecoration: 'none',
                      }}
                    >
                      {data.parentObjectIdNumber}
                    </Link>
                  )}

                  {((!data.caseUniqueId && !data.licenceUniqueId) ||
                    !hasPermissionsLicence) && (
                      <Typography
                        variant="body2"
                        sx={{ color: colors.primary500, fontWeight: 600 }}
                      >
                        {emptyFieldHolder(data.parentObjectIdNumber)}
                      </Typography>
                    )}
                </Typography>
              </Grid>
              <Grid sx={{ textAlign: "right" }}>
                <Button
                  variant={EButtonVariants.text}
                  icon={isExpanded ? EIconTypes.smallDropdownUp : EIconTypes.smallDropdownDown}
                  onClick={() => setIsExpanded(!isExpanded)}
                  tableButton
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.licenceName')}
            </Typography>
            <Typography variant="body2">{data.licenceName}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.licenceCode')}
            </Typography>
            <Typography variant="body2">{data.licenceCode}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.status')}
            </Typography>
            {data.licenceUniqueId &&
              data.parentObjectStatusUniqueId &&
              data.objectStatus !== '-' && (
                <Chips
                  text={data.objectStatus}
                  variant={getLicenceStatusChipVariant(data.parentObjectStatusUniqueId)}
                  fullWidth
                />
              )}
            {data.caseUniqueId &&
              data.parentObjectStatusUniqueId &&
              data.objectStatus !== '-' && (
                <Chips
                  text={data.objectStatus}
                  variant={renderStatusCaseFieldColor({
                    statusUniqueId: data.parentObjectStatusUniqueId,
                  })}
                  fullWidth
                />
              )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('legalEntityDetails.attachments.fields.numberOfFiles')}
            </Typography>
            <Typography variant="body2">
              {data.fileCount} {t('legalEntityDetails.attachments.fields.filesUnit')}
            </Typography>
          </Grid>
        </>
      )}
      <Grid size={{ xs: 12 }}>
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 2 }}>
            {details?.map((detail, index) => (
              <Box
                key={detail.id || index}
                sx={{
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.gray200}`,
                  borderRadius: '4px',
                  padding: '16px',
                  mb: 2,
                }}
              >
                <Grid sx={{ rowGap: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.attachments.fields.filename')}
                    </Typography>
                    <Typography variant="body2">{detail.fileName}</Typography>
                  </Grid>

                  {groupBy === ELegalAttachmentsGroupBy.NUMBER && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntityDetails.attachments.fields.attachmentType')}
                      </Typography>
                      <Typography variant="body2">{detail.attachmentType}</Typography>
                    </Grid>
                  )}

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.attachments.fields.createdDate')}
                    </Typography>
                    <Typography variant="body2">{renderDate(detail.createdDate)}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntityDetails.attachments.fields.addedBy')}
                    </Typography>
                    <Typography variant="body2">{detail.addedBy}</Typography>
                  </Grid>

                  {groupBy === ELegalAttachmentsGroupBy.ATTACHMENT_TYPE && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.attachments.fields.number')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary500, fontWeight: 600 }}
                        >
                          {detail.licenceUniqueId && type && hasPermissionsLicence && (
                            <Link
                              to={`${ERouteLinks.LegalEntityViewLicence.replace(
                                ':id',
                                detail.legalUniqueId,
                              ).replace(
                                ':licenceId',
                                detail.licenceUniqueId,
                              )}?type=${type}&from=le`}
                              style={{
                                color: colors.primary500,
                                fontWeight: 600,
                                textDecoration: 'none',
                              }}
                            >
                              {detail.parentObjectIdNumber}
                            </Link>
                          )}
                          {detail.caseUniqueId && hasPermissionsLicence && (
                            <Link
                              to={`${ERouteLinks.LegalEntityCasesDetailsPage.replace(
                                ':id',
                                detail.legalUniqueId,
                              ).replace(':caseId', detail.caseUniqueId)}?type=${type}&from=le`}
                              style={{
                                color: colors.primary500,
                                fontWeight: 600,
                                textDecoration: 'none',
                              }}
                            >
                              {detail.parentObjectIdNumber}
                            </Link>
                          )}

                          {((!detail.caseUniqueId && !detail.licenceUniqueId) ||
                            !hasPermissionsLicence) && (
                              <Typography
                                variant="body2"
                                sx={{ color: colors.primary500, fontWeight: 600 }}
                              >
                                {emptyFieldHolder(detail.parentObjectIdNumber)}
                              </Typography>
                            )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.attachments.fields.type')}
                        </Typography>
                        {detail.licenceUniqueId && (
                          <Chips
                            text={ATTACHMENT_TYPE_LICENCE}
                            variant={EChipsVariants.primary}
                          />
                        )}
                        {detail.caseUniqueId && (
                          <Chips
                            text={ATTACHMENT_TYPE_CASE}
                            variant={EChipsVariants.primary}
                            fullWidth
                          />
                        )}
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.attachments.fields.licenceCode')}
                        </Typography>
                        <Typography variant="body2">{detail.licenceCode || '-'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('legalEntityDetails.attachments.fields.status')}
                        </Typography>
                        {detail.licenceUniqueId &&
                          detail.parentObjectStatusUniqueId &&
                          detail.objectStatus !== '-' && (
                            <Chips
                              text={detail.objectStatus}
                              variant={getLicenceStatusChipVariant(
                                detail.parentObjectStatusUniqueId,
                              )}
                              fullWidth
                            />
                          )}
                        {detail.caseUniqueId &&
                          detail.parentObjectStatusUniqueId &&
                          detail.objectStatus !== '-' && (
                            <Chips
                              text={detail.objectStatus}
                              variant={renderStatusCaseFieldColor({
                                statusUniqueId: detail.parentObjectStatusUniqueId,
                              })}
                              fullWidth
                            />
                          )}
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid size={{ xs: 12, sm: detail.licenceUniqueId ? 6 : 4 }}>
                        <Button
                          id={`view-attachment-${detail.uniqueId}`}
                          variant={EButtonVariants.outlined}
                          icon={EIconTypes.view}
                          onClick={() =>
                            handleView(
                              detail.legalUniqueId as string,
                              detail.uniqueId as string,
                              detail.licenceUniqueId as string,
                              detail.caseUniqueId as string,
                            )
                          }
                          label={t('legalEntityDetails.attachments.view.button')}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: detail.licenceUniqueId ? 6 : 4 }}>
                        <Button
                          id={`download-attachment-${detail.uniqueId}`}
                          variant={EButtonVariants.outlined}
                          icon={EIconTypes.download}
                          onClick={() =>
                            handleDownload(
                              detail.legalUniqueId as string,
                              detail.uniqueId as string,
                              detail.fileName as string,
                              detail.licenceUniqueId as string,
                              detail.caseUniqueId as string,
                            )
                          }
                          label={t('legalEntityDetails.attachments.download.button')}
                          fullWidth
                        />
                      </Grid>
                      {!detail.licenceUniqueId && hasPermissionsAttachments && (
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Button
                            id={`delete-attachment-${detail.uniqueId}`}
                            variant={EButtonVariants.outlined}
                            icon={EIconTypes.delete}
                            onClick={() => handleDelete(detail.uniqueId as string)}
                            label={t('legalEntityDetails.attachments.delete.button')}
                            fullWidth
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Grid>
    </Grid>
  );
};

export { LegalEntityGroupedAttachmentTableCard };
